"use client";

import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  confirmContactEmail,
  requestContactEmailVerification,
  skipContactEmailPrompt,
} from "@/actions/contact-email";
import { OtpCodeInput } from "@/components/auth/otp-code-input";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { parseContactEmail } from "@/lib/identity/contact-email";

/**
 * The email ask: address, then the code that arrives in it.
 *
 * ## Why the copy is what it is
 *
 * The temptation is to make skipping feel risky — "you might lose your ticket".
 * That is not true: the ticket lives in the app, and the QR is in the wallet. The
 * first person who skips, tests it, and finds their ticket exactly where it
 * always was will trust nothing else the app tells them. So the skip copy names
 * the real trade-off — a copy in your inbox, useful when your phone dies at the
 * door — and the primary path is genuinely optional.
 *
 * ## Why verification exists at all
 *
 * Because a ticket gets mailed to this address. A typo does not usually land
 * nowhere; it lands on a real stranger, and mailing them a scannable QR is
 * gate-crashing plus a privacy breach. Unverified means in-app only.
 */

export type ContactEmailFormContext =
  | "event_checkout"
  | "guest_list"
  | "wallet_ticket"
  | "settings"
  | "onboarding"
  | "tier_join_request";

export type ContactEmailFormProps = {
  context: ContactEmailFormContext;
  /** Booking whose ticket should be mailed once the address is confirmed. */
  eventBookingId?: number | null;
  /** Pre-fills the field, e.g. from an OAuth address we already hold. */
  initialEmail?: string | null;
  /**
   * An address that already has a live code out. When set, the form opens on the
   * code step — the person typed the address at checkout and is here to finish.
   */
  pendingEmail?: string | null;
  onVerified?: (email: string) => void;
  /** Omit to hide the skip affordance (settings, where there is nothing to skip). */
  onSkip?: () => void;
  /** Wording of the skip link. Defaults to "Not now". */
  skipLabel?: string;
  /**
   * What skipping actually costs, in this surface's terms. The default is
   * written about tickets; a join request is not a ticket, and reusing that
   * copy there would describe a Wallet entry that does not exist.
   */
  skipNote?: ReactNode;
  autoFocus?: boolean;
};

const CODE_LENGTH = 6;

export function ContactEmailForm({
  context,
  eventBookingId,
  initialEmail,
  pendingEmail,
  onVerified,
  onSkip,
  skipLabel = "Not now",
  skipNote,
  autoFocus = true,
}: ContactEmailFormProps) {
  const [step, setStep] = useState<"email" | "code">(
    pendingEmail ? "code" : "email",
  );
  const [email, setEmail] = useState(pendingEmail ?? initialEmail ?? "");
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(
    pendingEmail ?? null,
  );
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const emailInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!autoFocus || step !== "email") return;
    // Two frames: a drawer's entrance transition steals focus on the first.
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => emailInputRef.current?.focus());
    });
    return () => cancelAnimationFrame(raf);
  }, [autoFocus, step]);

  const send = useCallback(
    async (candidate: string) => {
      setIsSending(true);
      setError(null);
      try {
        const result = await requestContactEmailVerification({
          email: candidate,
          context,
          eventBookingId: eventBookingId ?? null,
        });
        if (!result.ok) {
          setError(result.message);
          return;
        }
        setSubmittedEmail(result.data.email);
        setCode("");
        setStep("code");
      } catch {
        setError("Something went wrong. Please try again.");
      } finally {
        setIsSending(false);
      }
    },
    [context, eventBookingId],
  );

  // `useRef` rather than the `isConfirming` state: `onComplete` fires from an
  // effect, so reading state it is racing against would be stale.
  const confirmInFlight = useRef(false);

  const confirm = useCallback(
    async (submitted: string) => {
      if (confirmInFlight.current) return;
      confirmInFlight.current = true;
      setIsConfirming(true);
      setError(null);
      try {
        const result = await confirmContactEmail({ code: submitted });
        if (!result.ok) {
          setError(result.message);
          // An expired or exhausted code needs a new one, not another guess.
          if (result.code === "EXPIRED" || result.code === "NO_CANDIDATE") {
            setStep("email");
          }
          setCode("");
          return;
        }
        onVerified?.(result.data.email);
      } catch {
        setError("Something went wrong. Please try again.");
      } finally {
        confirmInFlight.current = false;
        setIsConfirming(false);
      }
    },
    [onVerified],
  );

  if (step === "email") {
    const parsed = parseContactEmail(email);
    return (
      <form
        className="flex w-full flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!parsed.ok) {
            setError("Enter a valid email address.");
            return;
          }
          void send(parsed.normalized);
        }}
      >
        <div className="flex flex-col gap-2">
          <label
            htmlFor="contact-email-input"
            className="text-sm font-medium text-text-secondary"
          >
            Email address
          </label>
          <input
            ref={emailInputRef}
            id="contact-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(event) => {
              setError(null);
              setEmail(event.target.value);
            }}
            placeholder="you@example.com"
            aria-invalid={Boolean(error)}
            className="h-12 w-full rounded-xl border border-border bg-surface-alt px-3 text-base font-medium text-text-primary outline-none placeholder:text-text-tertiary focus:border-brand dark:border-white/[0.12] dark:bg-glass-bg-06 dark:text-white dark:placeholder:text-white/38 dark:focus:border-white/35"
          />
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : (
            <p className="text-sm text-text-tertiary dark:text-white/55">
              {/*
                Say what THIS address will actually carry. A join request never
                produces a ticket or a receipt, and promising one here would be
                the first thing that never arrives.
              */}
              {context === "guest_list"
                ? "We'll send a code to confirm this address. It will be saved to your profile and can match an organizer's guest list."
                : context === "tier_join_request"
                  ? "We'll send a code to confirm it, then use it to tell you when the organisers decide."
                  : "We'll send a code to confirm it, then email you a copy of your tickets and receipts."}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSending || email.trim().length === 0}
          className="w-full rounded-xl bg-brand py-6 font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-60 dark:bg-brand dark:text-brand-foreground dark:hover:bg-brand/90"
        >
          {isSending ? <Spinner className="size-4" /> : "Send code"}
        </Button>

        {onSkip ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={isSkipping}
              onClick={() => {
                setIsSkipping(true);
                // Fire-and-forget: recording the dismissal must not make the
                // person wait to leave a screen they are declining.
                void skipContactEmailPrompt({ context }).finally(() => {
                  setIsSkipping(false);
                  onSkip();
                });
              }}
              className="py-1 text-center text-sm text-text-secondary underline underline-offset-4 transition-colors hover:text-text-primary disabled:opacity-60"
            >
              {skipLabel}
            </button>
            {/*
              Honest, and specific. NOT "you might lose your ticket" — the ticket
              is in the app either way, and the first person to test that claim
              would stop believing anything else we say.
            */}
            <p className="text-center text-xs leading-relaxed text-text-tertiary dark:text-white/45">
              {skipNote ?? (
                <>
                  Your tickets stay in the app under Wallet. Adding an email
                  just puts a copy in your inbox — handy if your phone dies at
                  the door.
                </>
              )}
            </p>
          </div>
        ) : null}
      </form>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <OtpCodeInput
        value={code}
        onChange={(value) => {
          setError(null);
          setCode(value);
        }}
        onComplete={(value) => void confirm(value)}
        length={CODE_LENGTH}
        invalid={Boolean(error)}
        busy={isConfirming}
        label="Enter the code"
        description={
          error ??
          (submittedEmail
            ? `Sent to ${submittedEmail}. Check your spam folder if it's not there.`
            : "Check your inbox.")
        }
        autoFocus
      />

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            setStep("email");
            setCode("");
            setError(null);
          }}
          className="text-sm text-text-secondary underline underline-offset-4 transition-colors hover:text-text-primary"
        >
          Change email
        </button>
        <button
          type="button"
          disabled={isSending}
          onClick={() => {
            if (submittedEmail) void send(submittedEmail);
          }}
          className="text-sm text-text-secondary underline underline-offset-4 transition-colors hover:text-text-primary disabled:opacity-50"
        >
          Resend code
        </button>
      </div>

      <Button
        type="button"
        disabled={isConfirming || code.length !== CODE_LENGTH}
        onClick={() => void confirm(code)}
        className="w-full rounded-xl bg-brand py-6 font-semibold text-brand-foreground hover:bg-brand/90 disabled:opacity-60 dark:bg-brand dark:text-brand-foreground dark:hover:bg-brand/90"
      >
        {isConfirming ? <Spinner className="size-4" /> : "Confirm email"}
      </Button>
    </div>
  );
}
